/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useCallback, useMemo } from 'react';

// Enhanced Custom hook for CKEditor style extraction and reverse conversion
export const useCKEditorStyleExtractor = () => {
  // HTML tag to CSS style mappings
  const tagStyleMap = useMemo(() => ({
    'i': { fontStyle: 'italic' },
    'em': { fontStyle: 'italic' },
    'b': { fontWeight: 'bold' },
    'strong': { fontWeight: 'bold' },
    'u': { textDecoration: 'underline' },
    's': { textDecoration: 'line-through' },
    'strike': { textDecoration: 'line-through' },
    'del': { textDecoration: 'line-through' },
    'sup': { verticalAlign: 'super', fontSize: 'smaller' },
    'sub': { verticalAlign: 'sub', fontSize: 'smaller' },
    'small': { fontSize: 'smaller' },
    'big': { fontSize: 'larger' },
    'mark': { backgroundColor: 'yellow' },
    'code': { fontFamily: 'monospace' },
    'kbd': { fontFamily: 'monospace', backgroundColor: '#f1f1f1', padding: '2px 4px', borderRadius: '3px' },
    'samp': { fontFamily: 'monospace' },
    'var': { fontStyle: 'italic', fontFamily: 'monospace' }
  }), []);

  // Reverse mapping: CSS styles to HTML tags (for reverse conversion)
  const styleTagMap = useMemo(() => {
    const reverseMap = {};
    
    // Create reverse mapping with priority for semantic tags
    const tagPriority = {
      'fontStyle-italic': 'i',
      'fontWeight-bold': 'b',
      'textDecoration-underline': 'u',
      'textDecoration-line-through': 's',
      'verticalAlign-super': 'sup',
      'verticalAlign-sub': 'sub',
      'fontSize-smaller': 'small',
      'fontSize-larger': 'big',
      'backgroundColor-yellow': 'mark'
    };

    Object.entries(tagPriority).forEach(([styleKey, tag]) => {
      reverseMap[styleKey] = tag;
    });

    return reverseMap;
  }, []);

  // CSS property mappings for camelCase conversion
  const cssPropertyMap = useMemo(() => ({
    'font-family': 'fontFamily',
    'font-size': 'fontSize',
    'font-weight': 'fontWeight',
    'font-style': 'fontStyle',
    'text-align': 'textAlign',
    'text-decoration': 'textDecoration',
    'text-transform': 'textTransform',
    'line-height': 'lineHeight',
    'letter-spacing': 'letterSpacing',
    'word-spacing': 'wordSpacing',
    'color': 'color',
    'background-color': 'backgroundColor',
    'background-image': 'backgroundImage',
    'background-size': 'backgroundSize',
    'background-position': 'backgroundPosition',
    'background-repeat': 'backgroundRepeat',
    'border': 'border',
    'border-color': 'borderColor',
    'border-width': 'borderWidth',
    'border-style': 'borderStyle',
    'border-radius': 'borderRadius',
    'margin': 'margin',
    'margin-top': 'marginTop',
    'margin-right': 'marginRight',
    'margin-bottom': 'marginBottom',
    'margin-left': 'marginLeft',
    'padding': 'padding',
    'padding-top': 'paddingTop',
    'padding-right': 'paddingRight',
    'padding-bottom': 'paddingBottom',
    'padding-left': 'paddingLeft',
    'width': 'width',
    'height': 'height',
    'max-width': 'maxWidth',
    'max-height': 'maxHeight',
    'min-width': 'minWidth',
    'min-height': 'minHeight',
    'display': 'display',
    'position': 'position',
    'top': 'top',
    'right': 'right',
    'bottom': 'bottom',
    'left': 'left',
    'float': 'float',
    'clear': 'clear',
    'overflow': 'overflow',
    'opacity': 'opacity',
    'z-index': 'zIndex',
    'box-shadow': 'boxShadow',
    'text-shadow': 'textShadow',
    'vertical-align': 'verticalAlign'
  }), []);

  // Convert CSS property to camelCase
  const toCamelCase = useCallback((property) => {
    return cssPropertyMap[property] || 
           property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }, [cssPropertyMap]);

  // Convert camelCase to kebab-case
  const toKebabCase = useCallback((property) => {
    return property.replace(/([A-Z])/g, '-$1').toLowerCase();
  }, []);

  // Parse inline styles from element.style object
  const parseInlineStyles = useCallback((styleDeclaration) => {
    const styles = {};

    for (let i = 0; i < styleDeclaration.length; i++) {
      const property = styleDeclaration[i];
      const value = styleDeclaration.getPropertyValue(property);
      
      if (value) {
        const camelCaseProperty = toCamelCase(property);
        styles[camelCaseProperty] = value.trim();
      }
    }

    return styles;
  }, [toCamelCase]);

  // Extract styles based on semantic HTML tags
  const extractTagStyles = useCallback((element) => {
    const tagName = element.tagName.toLowerCase();
    return tagStyleMap[tagName] || {};
  }, [tagStyleMap]);

  // Extract nested/inherited styles from parent elements
  const extractNestedStyles = useCallback((element) => {
    const nestedStyles = {};
    let currentElement = element.parentElement;

    // Traverse up the DOM tree to collect inherited styles
    while (currentElement && currentElement.tagName !== 'BODY') {
      const tagName = currentElement.tagName.toLowerCase();
      
      // Add tag-based styles from parents
      if (tagStyleMap[tagName]) {
        Object.entries(tagStyleMap[tagName]).forEach(([prop, value]) => {
          // Only add if not already defined (child takes precedence)
          if (!nestedStyles[prop]) {
            nestedStyles[prop] = value;
          }
        });
      }

      // Add inline styles from parents that can be inherited
      if (currentElement.style.length > 0) {
        const inheritableProps = ['color', 'fontFamily', 'fontSize', 'lineHeight', 'textAlign'];
        const parentStyles = parseInlineStyles(currentElement.style);
        
        inheritableProps.forEach(prop => {
          if (parentStyles[prop] && !nestedStyles[prop]) {
            nestedStyles[prop] = parentStyles[prop];
          }
        });
      }

      currentElement = currentElement.parentElement;
    }

    return nestedStyles;
  }, [tagStyleMap, parseInlineStyles]);

  // Extract image-specific styles
  const extractImageStyles = useCallback((imgElement, styleData) => {
    const imageStyles = {};

    if (imgElement.width) imageStyles.width = `${imgElement.width}px`;
    if (imgElement.height) imageStyles.height = `${imgElement.height}px`;
    if (imgElement.getAttribute('align')) {
      imageStyles.textAlign = imgElement.getAttribute('align');
    }

    // Merge with existing styles
    Object.assign(styleData.styles, imageStyles);
    
    // Add image-specific metadata
    styleData.src = imgElement.src;
    styleData.alt = imgElement.alt;
  }, []);

  // Main extraction function
  const extractStyles = useCallback((editorContent, options = {}) => {
    const {
      includeInlineStyles = true,
      includeTagStyles = true,
      includeComputedStyles = false,
      groupByElement = false,
      filterEmptyStyles = true,
      mergeNestedStyles = true
    } = options;

    // Create a temporary DOM element to parse the content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editorContent;

    const results = [];
    const elements = tempDiv.querySelectorAll('*');

    elements.forEach((element, index) => {
      const styleData = {
        elementType: element.tagName.toLowerCase(),
        elementIndex: index,
        className: element.className || null,
        id: element.id || null,
        styles: {},
        textContent: element.textContent?.trim() || null
      };

      // Extract inline styles
      if (includeInlineStyles && element.style.length > 0) {
        styleData.styles = { ...styleData.styles, ...parseInlineStyles(element.style) };
      }

      // Extract semantic tag styles (like <i>, <b>, etc.)
      if (includeTagStyles) {
        const tagStyles = extractTagStyles(element);
        styleData.styles = { ...styleData.styles, ...tagStyles };
      }

      // Extract nested styling context
      if (mergeNestedStyles) {
        const nestedStyles = extractNestedStyles(element);
        styleData.styles = { ...styleData.styles, ...nestedStyles };
        styleData.inheritedStyles = nestedStyles;
      }

      // Handle special cases for images
      if (element.tagName.toLowerCase() === 'img') {
        extractImageStyles(element, styleData);
      }

      // Filter empty styles if requested
      if (filterEmptyStyles && Object.keys(styleData.styles).length === 0 && !styleData.textContent) {
        return;
      }

      results.push(styleData);
    });

    return results;
  }, [parseInlineStyles, extractTagStyles, extractNestedStyles, extractImageStyles]);

  // Extract styles specifically for formatting tags
  const extractFormattingStyles = useCallback((editorContent) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editorContent;
    
    const formattingTags = ['i', 'em', 'b', 'strong', 'u', 's', 'strike', 'del', 'sup', 'sub'];
    const formattingStyles = {};

    formattingTags.forEach(tag => {
      const elements = tempDiv.querySelectorAll(tag);
      elements.forEach((element, index) => {
        const key = `${tag}_${index}`;
        formattingStyles[key] = {
          ...tagStyleMap[tag],
          textContent: element.textContent,
        };
      });
    });

    return formattingStyles;
  }, [tagStyleMap]);

  // NEW: Convert styles object to HTML with semantic tags
  const stylesToHtml = useCallback((text, styles = {}, options = {}) => {
    const {
      preferSemanticTags = true,
      wrapInParagraph = true,
      preserveWhitespace = true
    } = options;

    if (!text || typeof text !== 'string') {
      return '';
    }

    let wrappedText = text;
    const inlineStyles = {};

    // Process each style property
    Object.entries(styles).forEach(([property, value]) => {
      const styleKey = `${property}-${value}`;
      
      // Check if this style can be converted to a semantic tag
      if (preferSemanticTags && styleTagMap[styleKey]) {
        const tag = styleTagMap[styleKey];
        wrappedText = `<${tag}>${wrappedText}</${tag}>`;
      } else {
        // Add to inline styles for non-semantic styles
        inlineStyles[property] = value;
      }
    });

    // Apply remaining inline styles
    if (Object.keys(inlineStyles).length > 0) {
      const styleAttr = Object.entries(inlineStyles)
        .map(([prop, val]) => `${toKebabCase(prop)}: ${val}`)
        .join('; ');
      
      wrappedText = `<span style="${styleAttr}">${wrappedText}</span>`;
    }

    // Wrap in paragraph if requested
    if (wrapInParagraph) {
      wrappedText = `<p>${wrappedText}</p>`;
    }

    return wrappedText;
  }, [styleTagMap, toKebabCase]);

  // NEW: Convert multiple styled text segments to HTML
  const multipleStylesToHtml = useCallback((segments, options = {}) => {
    const {
      wrapInContainer = true,
      containerTag = 'div',
      separateElements = false
    } = options;

    if (!Array.isArray(segments)) {
      return '';
    }

    const htmlSegments = segments.map(segment => {
      const { text, styles = {}, wrapInParagraph = false } = segment;
      return stylesToHtml(text, styles, { ...options, wrapInParagraph });
    });

    let result = separateElements ? htmlSegments.join('\n') : htmlSegments.join(' ');

    if (wrapInContainer) {
      result = `<${containerTag}>${result}</${containerTag}>`;
    }

    return result;
  }, [stylesToHtml]);

  // NEW: Apply styles to existing HTML content
  const applyStylesToHtml = useCallback((htmlContent, styles = {}, targetSelector = '*') => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    const targetElements = targetSelector === '*' 
      ? tempDiv.querySelectorAll('*') 
      : tempDiv.querySelectorAll(targetSelector);

    targetElements.forEach(element => {
      Object.entries(styles).forEach(([property, value]) => {
        const styleKey = `${property}-${value}`;
        
        // Check if this style should be converted to semantic tag
        if (styleTagMap[styleKey]) {
          const tag = styleTagMap[styleKey];
          const wrapper = document.createElement(tag);
          
          // Move the element's content to the wrapper
          while (element.firstChild) {
            wrapper.appendChild(element.firstChild);
          }
          
          element.appendChild(wrapper);
        } else {
          // Apply as inline style
          const kebabProperty = toKebabCase(property);
          element.style[kebabProperty] = value;
        }
      });
    });

    return tempDiv.innerHTML;
  }, [styleTagMap, toKebabCase]);

  // NEW: Extract and convert - one-step process
  const extractAndConvert = useCallback((editorContent, targetStyles = {}) => {
    const extracted = extractStyles(editorContent);
    const converted = {};

    extracted.forEach(item => {
      const key = item.className || item.id || `${item.elementType}_${item.elementIndex}`;
      const mergedStyles = { ...item.styles, ...targetStyles };
      
      if (item.textContent) {
        converted[key] = stylesToHtml(item.textContent, mergedStyles, { wrapInParagraph: false });
      }
    });

    return converted;
  }, [extractStyles, stylesToHtml]);

  // Generate CSS string from extracted styles
  const generateCSS = useCallback((extractedStyles) => {
    let css = '';
    
    extractedStyles.forEach((item, index) => {
      const selector = item.className 
        ? `.${item.className}` 
        : item.id 
        ? `#${item.id}` 
        : `${item.elementType}-${index}`;

      const styleRules = Object.entries(item.styles)
        .map(([prop, value]) => `  ${toKebabCase(prop)}: ${value};`)
        .join('\n');

      if (styleRules) {
        css += `${selector} {\n${styleRules}\n}\n\n`;
      }
    });

    return css;
  }, [toKebabCase]);

  // Get flattened styles object
  const getFlattenedStyles = useCallback((extractedStyles) => {
    const flattened = {};
    
    extractedStyles.forEach((item, index) => {
      const prefix = item.className || item.id || `${item.elementType}_${index}`;
      Object.entries(item.styles).forEach(([prop, value]) => {
        flattened[`${prefix}_${prop}`] = value;
      });
    });

    return flattened;
  }, []);

  const getSingleStyles = useCallback((extractedStyles) => {
    const flattened = {};
    
    extractedStyles.forEach((item, index) => {
      // const prefix = item.className || item.id || `${item.elementType}_${index}`;
      Object.entries(item.styles).forEach(([prop, value]) => {
        flattened[prop] = value;
      });
    });

    return flattened;
  }, []);

  return {
    // Original functions
    extractStyles,
    extractFormattingStyles,
    generateCSS,
    getFlattenedStyles,
    getSingleStyles,
    
    
    // New reverse conversion functions
    stylesToHtml,
    multipleStylesToHtml,
    applyStylesToHtml,
    extractAndConvert
  };
};